"use client";

import { BellIcon, DotIcon, Loader } from "@/components/common/icons";
import {
  NotificationWithComedian,
  markAllAsRead,
  markAsRead,
} from "@/data/web-notifications";
import { Popover, PopoverContent, PopoverTrigger } from "@/shadcn/ui/popover";
import { WebNotificationsEvents, trackEvent } from "@/analytics";
import {
  inAppFeedKeys,
  useInAppFeed,
  useMarkFeedNotificationAsRead,
} from "@/hooks/useInAppFeed";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/shadcn/ui/button";
import { Comedian } from "@/data/comedians";
import Link from "next/link";
import NotificationWithComedianAvatar from "./NotificationWithComedianAvatar";
import { Separator } from "@/shadcn/ui/separator";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import dayjs from "@punchup/utils/dayjs";
import { getCDNImageURL } from "@/data/images";
import { getComedianFromV2Payload } from "./v2-payload-utils";
import { getShow } from "@/data/shows";
import { getSpecialByID } from "@/data/specials";
import { markAllFeedNotificationsAsRead } from "@/data/v2/client/notifications/feed";
import { useGate } from "statsig-react";
import { useLastActiveAt } from "@punchup/data/legacy/hooks";
import { useNotificationsUpdater } from "./useNotificationsUpdater";
import { useQueryClient } from "@tanstack/react-query";

export type NotificationType = {
  // An individual notification id. Can also be a random generated number between 0 and 1000 for grouped notifications.
  id: string;
  // A group of notification ids that identify a grouped notification (example: Sam Morril added 15 new shows)
  group_of_notification_ids?: string[];
  // A grouped notification can belong to one or more comedian. When it refers to just one comedian, this will be true.
  singleComedian?: boolean;
  // This refers to when the event that the notification is about happened, not the time of creation of the notification
  related_timestamp: string;
  title: JSX.Element | string;
  read: boolean;
  link?: string;
  contentType: string;
  relatedComedian: string;
  source: "v1" | "v2";
};

// As we keep exploring, I want to keep this separate from NotificationClientWrapper.tsx even though they are very similar
const NotificationItem = ({
  id,
  title,
  link,
  read,
  group_of_notification_ids,
  onClearNotification,
  singleComedian,
  onClick,
  contentType,
  relatedComedian,
  source,
  onMarkV2AsRead,
}: Pick<
  NotificationType,
  | "id"
  | "title"
  | "link"
  | "read"
  | "group_of_notification_ids"
  | "singleComedian"
  | "contentType"
  | "relatedComedian"
  | "source"
> & {
  onClearNotification: (id: string) => void;
  onClick: () => void;
  onMarkV2AsRead?: (id: string) => void;
}) => {
  const supabase = createClientComponentClient();

  const clearNotification = useCallback(async () => {
    if (source === "v2") {
      onMarkV2AsRead?.(id);
      onClearNotification(id);
      return;
    }

    // Validate that this is a valid uuid and not the Math.random number
    // used to identify a grouped notification.
    if (id.length === 36) {
      trackEvent(WebNotificationsEvents.CLICKED_ON_NOTIFICATION, {
        isGroupedNotification: false,
        location: "notifications_bell",
        notificationType: contentType,
        relatedComedian: relatedComedian,
      });
      markAsRead(supabase, [{ notification_id: id }]);
      onClearNotification(id);
    } else {
      trackEvent(WebNotificationsEvents.CLICKED_ON_NOTIFICATION, {
        isGroupedNotification: true,
        location: "notifications_bell",
        notificationType: contentType,
        relatedComedian: relatedComedian,
      });
      if (group_of_notification_ids && group_of_notification_ids.length > 0) {
        // This is just an additional safety flag - so that we only clear notifications relative to a single comedian
        // What is this exactly? Basically, when a single comedian has many tour dates - we group those shows into one notification
        // However we need to distinguish when all these shows belong to just one comedian (clear them -> redirect to /comedian-slug/tickets)
        // or not (don't clear -> redirect to /notifications).
        if (singleComedian) {
          markAsRead(
            supabase,
            group_of_notification_ids.map((id) => ({
              notification_id: id,
            }))
          );
          onClearNotification(id);
        }
      }
    }
  }, [
    contentType,
    group_of_notification_ids,
    id,
    onClearNotification,
    onMarkV2AsRead,
    relatedComedian,
    singleComedian,
    source,
    supabase,
  ]);

  const notificationStyle =
    "flex min-h-[3rem] py-0.5 text-slate-800 px-1 w-full items-center";
  return (
    <span
      className="flex items-center cursor-pointer hover:bg-slate-100"
      onClick={onClick}
    >
      {!read && (
        <span className="px-2">
          <DotIcon className="text-notification-red" width={12} height={12} />
        </span>
      )}
      {link ? (
        <Link
          href={link}
          onClick={clearNotification}
          className={notificationStyle}
        >
          {" "}
          {title}
        </Link>
      ) : (
        <span onClick={clearNotification} className={notificationStyle}>
          {title}{" "}
        </span>
      )}
    </span>
  );
};

export function NotificationsBell() {
  const lastActiveAt = useLastActiveAt();
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [newComedians, setNewComedians] = useState<NotificationWithComedian[]>(
    []
  );
  const [newSpecials, setNewSpecials] = useState<NotificationWithComedian[]>(
    []
  );
  const [newTourDates, setNewTourDates] = useState<NotificationWithComedian[]>(
    []
  );
  const [comediansPerforming, setComediansPerforming] = useState<Comedian[]>(
    []
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [requestedNotificationUpdateTime, setRequestedNotificationUpdateTime] =
    useState<number>(0);
  const { timeUpdateWasRequested } = useNotificationsUpdater();

  const { value: isInAppFeedV2 } = useGate("in_app_feed_v2");
  const { data: v2FeedData, isLoading: isV2FeedLoading } =
    useInAppFeed(isInAppFeedV2);
  const markV2AsRead = useMarkFeedNotificationAsRead();
  const queryClient = useQueryClient();

  const supabase = createClientComponentClient();

  const clearAll = useCallback(async () => {
    trackEvent(WebNotificationsEvents.CLICKED_MARK_ALL_AS_READ, {
      location: "notifications_bell",
    });
    await markAllAsRead(supabase);

    if (isInAppFeedV2) {
      await markAllFeedNotificationsAsRead();
      queryClient.invalidateQueries({ queryKey: inAppFeedKeys.all });
    }

    setNotifications([]);
  }, [isInAppFeedV2, queryClient, supabase]);

  const fetchData = useCallback(async () => {
    if (lastActiveAt) {
      setIsLoading(true);
      const response = await fetch("/api/collect-notifications", {
        method: "POST",
        body: JSON.stringify({ lastActiveAt }),
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const {
          new_comedians,
          new_specials,
          new_tour_dates,
          comedians_performing,
        } = await response.json();
        setNewComedians(
          new_comedians.filter(
            (notification: NotificationWithComedian) => !notification.read
          )
        );
        setNewSpecials(
          new_specials.filter(
            (notification: NotificationWithComedian) => !notification.read
          )
        );
        const tourDatesNonYetRead = new_tour_dates.filter(
          (notification: NotificationWithComedian) => !notification.read
        );
        setNewTourDates(tourDatesNonYetRead);
        setComediansPerforming(
          comedians_performing.filter((comedian: Comedian) =>
            tourDatesNonYetRead
              .map(
                (notification: NotificationWithComedian) =>
                  notification.comedian_id
              )
              .includes(comedian.id)
          )
        );
      } else {
        console.error("There was an issue collecting notifications");
      }
      setIsLoading(false);
    }
  }, [lastActiveAt]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setRequestedNotificationUpdateTime(timeUpdateWasRequested);
  }, [timeUpdateWasRequested]);

  // This is used to listen for requests to refresh notifications from other components
  useEffect(() => {
    if (timeUpdateWasRequested > requestedNotificationUpdateTime) {
      fetchData();
    }
  }, [fetchData, requestedNotificationUpdateTime, timeUpdateWasRequested]);

  useEffect(() => {
    const calculateNotifications = async () => {
      const notis: NotificationType[] = [];
      if (newComedians.length > 0) {
        // If we have 3 or less new comedians, we display them as separate notifications
        // otherwise we group them (see else statement below)
        if (newComedians.length <= 3) {
          for (const newComedian of newComedians) {
            const { read, notification_id, comedian } = newComedian;
            const comedianPictureUrl = getCDNImageURL(
              comedian?.about_img || ""
            );
            notis.push({
              id: notification_id,
              related_timestamp: "",
              title: (
                <span className="py-1">
                  <NotificationWithComedianAvatar
                    comedianImgUrl={comedianPictureUrl}
                    comedianName={comedian?.display_name || ""}
                    text={"joined!"}
                  />
                </span>
              ),
              link: `/${comedian?.slug}`,
              read,
              contentType: "new_comedian",
              relatedComedian: comedian?.id || "",
              source: "v1",
            });
          }
        } else {
          notis.push({
            id: Math.floor(Math.random() * 1000).toString(),
            related_timestamp: "",
            title: `There are ${newComedians.length} new comedians available!`,
            link: `/notifications`,
            read: !newComedians.some((notification) => !notification.read),
            contentType: "new_comedian",
            relatedComedian: "multiple",
            source: "v1",
          });
        }
      }
      if (newSpecials.length > 0) {
        if (newSpecials.length === 1) {
          const notification = newSpecials[0];
          const fullSpecial = await getSpecialByID(notification.content_id!);
          notis.push({
            id: notification.notification_id,
            related_timestamp: "",
            title: `${fullSpecial.title} by ${notification.comedian?.display_name} is now available!`,
            link: `/${notification.comedian?.slug}/${fullSpecial.slug}`,
            read: notification.read,
            contentType: "new_special",
            relatedComedian: fullSpecial.comedian_id,
            source: "v1",
          });
        } else {
          notis.push({
            id: Math.floor(Math.random() * 1000).toString(),
            related_timestamp: "",
            title: `There are ${newSpecials.length} new videos available!`,
            link: `/notifications`,
            read: !newSpecials.some((notification) => !notification.read),
            contentType: "new_special",
            relatedComedian: "multiple",
            source: "v1",
          });
        }
      }

      if (comediansPerforming.length > 0) {
        if (comediansPerforming.length === 1) {
          const comedianPerforming = comediansPerforming[0];
          const notificationsForThisPerformer = newTourDates.filter(
            (notification) => notification.comedian_id === comedianPerforming.id
          );
          if (notificationsForThisPerformer.length === 1) {
            const singleNoti = notificationsForThisPerformer[0];
            const { notification_id, related_timestamp, content_id } =
              singleNoti;
            const fullShow = await getShow(content_id!);
            const showLocation = fullShow?.venue || undefined;
            const showDate = dayjs
              .utc(fullShow?.datetime || "")
              .format("MMMM D, YYYY");

            notis.push({
              id: notification_id,
              related_timestamp: related_timestamp || "",
              title: `${comedianPerforming.display_name} added a new show ${
                showLocation ? "at " + showLocation : ""
              } for ${showDate}!`,
              link: `/show/${content_id}`,
              read: !notificationsForThisPerformer.some(
                (notification) => !notification.read
              ),
              group_of_notification_ids: notificationsForThisPerformer.map(
                (notification) => notification.notification_id
              ),
              singleComedian: true,
              contentType: "new_tour_date",
              relatedComedian: comedianPerforming.id,
              source: "v1",
            });
          } else {
            notis.push({
              id: Math.floor(Math.random() * 1000).toString(),
              related_timestamp: "",
              title: `New tour dates added by ${comedianPerforming.display_name}!`,
              link: `/${comedianPerforming.slug}/tickets`,
              read: !notificationsForThisPerformer.some(
                (notification) => !notification.read
              ),
              group_of_notification_ids: notificationsForThisPerformer.map(
                (notification) => notification.notification_id
              ),
              singleComedian: true,
              contentType: "new_tour_date",
              relatedComedian: comedianPerforming.id,
              source: "v1",
            });
          }
        } else if (comediansPerforming.length === 2) {
          notis.push({
            id: Math.floor(Math.random() * 1000).toString(),
            related_timestamp: "",
            title: `New tour dates added by ${comediansPerforming[0].display_name} and ${comediansPerforming[1].display_name}!`,
            link: `/notifications`,
            read: false,
            contentType: "new_tour_date",
            relatedComedian: "multiple",
            source: "v1",
          });
        } else {
          notis.push({
            id: Math.floor(Math.random() * 1000).toString(),
            related_timestamp: "",
            title: `New tour dates added by ${comediansPerforming[0].display_name}, ${comediansPerforming[1].display_name} and more!`,
            link: `/notifications`,
            read: false,
            contentType: "new_tour_date",
            relatedComedian: "multiple",
            source: "v1",
          });
        }
      }

      if (isInAppFeedV2 && v2FeedData?.items) {
        for (const item of v2FeedData.items) {
          if (item.is_read) continue;
          const comedian = getComedianFromV2Payload(item.payload);
          const imageUrl = getCDNImageURL(
            comedian.imageUrl || item.image_url || ""
          );
          const displayText =
            item.notification_type_id === "followed_comedian_show"
              ? item.title || ""
              : item.body || item.title || "";

          notis.push({
            id: item.id,
            related_timestamp: item.created_at || "",
            title: (
              <span className="py-1">
                <NotificationWithComedianAvatar
                  comedianImgUrl={imageUrl}
                  comedianName={comedian.name}
                  text={displayText.replace(comedian.name, "").trim()}
                />
              </span>
            ),
            link: item.action_url || undefined,
            read: false,
            source: "v2",
            contentType: item.notification_type_id || "",
            relatedComedian: item.comedian_id || comedian.id || comedian.slug,
          });
        }
      }

      setNotifications(notis);
    };

    const shouldWaitForV2 = isInAppFeedV2 && isV2FeedLoading;
    if (!isLoading && !shouldWaitForV2) {
      calculateNotifications();
    }
  }, [
    comediansPerforming,
    isInAppFeedV2,
    isLoading,
    isV2FeedLoading,
    newComedians,
    newSpecials,
    newTourDates,
    v2FeedData,
  ]);

  const onClearNotificationCallback = useCallback((id: string) => {
    setNotifications((prevState) =>
      prevState.filter((notification) => notification.id !== id)
    );
  }, []);

  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <button
          aria-label="Notifications"
          className="relative px-2 py-1.5 md:p-2 md:hover:bg-[#e5d6c3]/50 rounded-md transition-colors ease-in-out duration-200"
          onClick={() => {
            trackEvent(WebNotificationsEvents.CLICKED_NOTIFICATION_BELL);
            setOpen(!open);
          }}
        >
          <BellIcon width={24} height={24} className="stroke-primary-blue" />
          {notifications && notifications.length > 0 && (
            <DotIcon
              className="absolute top-1 right-1.5 md:top-[6px] md:right-[6px] z-10 border rounded-full text-notification-red fill-notification-red border-ivory"
              width={13}
              height={13}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="flex max-h-[60vh] w-[350px] flex-col p-0"
        align="center"
        onInteractOutside={() => setOpen(false)}
      >
        <span className="flex justify-between items-center py-2.5 px-3">
          <h5 className="text-slate-800">Notifications</h5>
          <Button
            variant="ghost"
            className="px-2 text-sm leading-none text-muted-foreground hover:text-muted-foreground focus-visible:ring-transparent"
            onClick={clearAll}
            disabled={notifications && notifications.length === 0}
          >
            Mark All as Read
          </Button>
        </span>
        <Separator className="my-1 bg-muted" />
        <div className="min-h-0 flex-1 overflow-y-auto">
          {notifications && notifications.length > 0 ? (
            notifications.map((notification) => {
              const {
                id,
                title,
                link,
                read,
                group_of_notification_ids,
                singleComedian,
                contentType,
                relatedComedian,
                source,
              } = notification;
              return (
                <NotificationItem
                  key={id}
                  id={id}
                  title={title}
                  link={link}
                  read={read}
                  group_of_notification_ids={group_of_notification_ids}
                  singleComedian={singleComedian}
                  onClearNotification={onClearNotificationCallback}
                  onClick={() => setOpen(false)}
                  contentType={contentType}
                  relatedComedian={relatedComedian}
                  source={source}
                  onMarkV2AsRead={(nId) =>
                    markV2AsRead.mutate({
                      notificationId: nId,
                      location: "notifications_bell",
                    })
                  }
                />
              );
            })
          ) : (
            <div className="py-2">
              {isLoading || (isInAppFeedV2 && isV2FeedLoading) ? (
                <div className="flex items-center justify-center h-12">
                  <Loader height={6} width={6} />{" "}
                </div>
              ) : (
                <p className="flex items-center justify-center py-6 text-lg pointer-events-none text-slate-600">
                  <span className="px-2 text-2xl font-emoji">🎉</span>
                  All caught up!{" "}
                </p>
              )}
            </div>
          )}
        </div>
        <Separator className="mt-2 bg-muted" />
        <Link
          className="flex items-center justify-center px-1 py-3 text-sm text-slate-600 hover:bg-slate-100"
          href={"/notifications"}
          onClick={() => {
            trackEvent(
              WebNotificationsEvents.CLICKED_ON_VIEW_ALL_NOTIFICATIONS
            );
            setOpen(false);
          }}
        >
          {" "}
          View All Notifications{" "}
        </Link>
      </PopoverContent>
    </Popover>
  );
}
